export type PermissionAction = {
  id: number;
  action_name: string;
  display_name: string;
  module: {
    id: number;
    module_name: string;
    display_name: string;
    path: string | null;
    element_name: string | null;
    sequence?: number | null;
  };
};

export type PermissionModule = {
  id: number;
  module_name: string;
  display_name: string;
  path: string | null;
  element_name: string | null;
  sequence?: number | null;
  actions: Array<{
    id: number;
    action_name: string;
    display_name: string;
  }>;
};

export type PermissionRole = {
  id: number;
  name: string;
  display_name: string;
  actions: PermissionAction[];
  created_at: string;
  updated_at: string;
};

export type RoleMapping = {
  user_id: number;
  role_id: number;
  role: PermissionRole;
};

export type PermissionUser = {
  id: number;
  email: string;
  full_name: string;
  is_active: boolean;
  manager_id: number | null;
  organization_id: number;
  role_mappings: RoleMapping[];
};

export type UserRole = Omit<PermissionRole, "actions">;

export type PermissionUserView = Omit<PermissionUser, "role_mappings"> & {
  roles: UserRole[];
  modules: PermissionModule[];
};

export function getRoleMappingActions(roleMappings: RoleMapping[] = []): PermissionAction[] {
  return roleMappings.flatMap((mapping) => mapping.role.actions);
}

export function buildPermissionModules(actions: PermissionAction[]): PermissionModule[] {
  const modules = new Map<string, PermissionModule>();

  actions.forEach((action) => {
    const moduleKey = String(action.module.id);
    const actionKey = String(action.id);
    const permissionModule = modules.get(moduleKey) ?? {
      id: action.module.id,
      module_name: action.module.module_name.trim(),
      display_name: action.module.display_name,
      path: action.module.path,
      element_name: action.module.element_name,
      sequence: action.module.sequence,
      actions: [],
    };

    if (!permissionModule.actions.some((item) => String(item.id) === actionKey)) {
      permissionModule.actions.push({
        id: action.id,
        action_name: action.action_name,
        display_name: action.display_name,
      });
    }

    modules.set(moduleKey, permissionModule);
  });

  return Array.from(modules.values());
}

export function buildPermissionUser(user: PermissionUser): PermissionUserView {
  const { role_mappings: roleMappings, ...userProfile } = user;
  const roles = Array.from(new Map(roleMappings.map(({ role }) => [role.id, role])).values()).map(
    ({ actions: _actions, ...role }) => role
  );

  return {
    ...userProfile,
    roles,
    modules: buildPermissionModules(getRoleMappingActions(roleMappings)),
  };
}

export function getPermissionModules(
  user?: PermissionUser | PermissionUserView
): PermissionModule[] {
  if (!user) return [];

  if ("modules" in user) {
    return user.modules;
  }

  return buildPermissionModules(getRoleMappingActions(user.role_mappings));
}

export function hasModulePermission(
  user: PermissionUser | PermissionUserView | undefined,
  moduleName?: string
): boolean {
  if (!moduleName) return true;

  return getPermissionModules(user).some((module) => module.module_name === moduleName);
}

export function hasModuleAction(
  user: PermissionUser | PermissionUserView | undefined,
  moduleName: string,
  actionName: string
): boolean {
  const permissionModule = getPermissionModules(user).find(
    (module) => module.module_name === moduleName
  );

  return !!permissionModule?.actions.some((action) => action.action_name === actionName);
}
